import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Users, Shield, Trash2, AlertTriangle } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success(t('savedSuccessfully'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, { withCredentials: true });
      toast.success(t('deletedSuccessfully'));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <Skeleton className="h-96 bg-slate-800" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="users-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white font-['Comfortaa'] flex items-center gap-3">
            <Shield className="h-8 w-8 text-[#b4976f]" />
            {t('userManagement')}
          </h1>
          <p className="text-slate-400 mt-1">
            {users.length} registered users
          </p>
        </div>

        {/* Users Table */}
        <Card className="bg-[#1e293b] border-slate-700">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">User</TableHead>
                  <TableHead className="text-slate-400">{t('email')}</TableHead>
                  <TableHead className="text-slate-400">{t('role')}</TableHead>
                  <TableHead className="text-slate-400 text-right">{t('edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.user_id} className="border-slate-700 table-row-hover">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-[#b4976f] text-white">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.user_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">{user.email}</TableCell>
                    <TableCell>
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.user_id, value)}
                        disabled={user.user_id === currentUser?.user_id}
                      >
                        <SelectTrigger 
                          className={`w-32 ${
                            user.role === 'admin' 
                              ? 'bg-[#b4976f]/20 border-[#b4976f]/50 text-[#b4976f]' 
                              : 'bg-[#264162]/50 border-[#264162] text-[#c8b599]'
                          }`}
                          data-testid={`role-select-${user.user_id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="admin" className="text-white hover:bg-slate-800">
                            {t('admin')}
                          </SelectItem>
                          <SelectItem value="investor" className="text-white hover:bg-slate-800">
                            {t('investor')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      {user.user_id !== currentUser?.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-400" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Role Info */}
        <Card className="bg-[#264162]/30 border-[#b4976f]/30">
          <CardContent className="p-4">
            <h3 className="text-white font-semibold mb-3">Role Permissions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-[#b4976f] font-medium mb-2">{t('admin')}</p>
                <ul className="text-slate-400 space-y-1">
                  <li>• Full CRUD access to all data</li>
                  <li>• Manage users and roles</li>
                  <li>• Delete loans, properties, tenants</li>
                </ul>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-[#c8b599] font-medium mb-2">{t('investor')}</p>
                <ul className="text-slate-400 space-y-1">
                  <li>• Create and read data</li>
                  <li>• View all dashboards and reports</li>
                  <li>• Cannot update or delete records</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#1e293b] border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                {t('confirmDelete')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                This will permanently delete user "{selectedUser?.name}" and all their sessions.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-800">
                {t('cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-user-btn"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
